import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { authFetch } from "../api";

export default function ProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Profile data
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState("");

  // Neighborhood preferences
  const [defaultNeighborhoods, setDefaultNeighborhoods] = useState([]);
  const [availableNeighborhoods, setAvailableNeighborhoods] = useState([]);
  const [neighborhoodDropdownOpen, setNeighborhoodDropdownOpen] = useState(false);
  const [savingNeighborhoods, setSavingNeighborhoods] = useState(false);

  // Notification preferences
  const [notifications, setNotifications] = useState({
    high_cap_rate: { enabled: false, threshold: 5, neighborhood_ids: [] },
    below_prev_sale: { enabled: false, threshold: 0, neighborhood_ids: [] },
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const [belowSaleDropdownOpen, setBelowSaleDropdownOpen] = useState(false);

  // Password change
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) navigate("/login");
    else {
      loadProfile();
      loadNeighborhoods();
      loadNotifications();
    }
  }, [navigate]);

  const loadProfile = async () => {
    try {
      const data = await authFetch("/profile");
      setEmail(data.email);
      setFirstName(data.first_name || "");
      setLastName(data.last_name || "");
      setProfilePictureUrl(data.profile_picture_url || "");
      // Store raw default_neighborhoods - will convert to IDs after neighborhoods load
      setDefaultNeighborhoods(data.default_neighborhoods || []);
    } catch (err) {
      console.error("Error loading profile:", err);
      setMessage({ type: "error", text: "Failed to load profile" });
    } finally {
      setLoading(false);
    }
  };

  const loadNeighborhoods = async () => {
    try {
      const data = await authFetch("/sale-reviews/neighborhoods");
      setAvailableNeighborhoods(data.neighborhoods || []);

      // Convert any legacy string names to IDs in default_neighborhoods
      // This needs to happen after we have the neighborhoods list
      if (data.default_neighborhoods && data.default_neighborhoods.length > 0) {
        const convertedIds = data.default_neighborhoods.map(item => {
          // If it's already a number, use it
          if (typeof item === 'number') return item;
          // If it's a string that looks like a number, parse it
          if (typeof item === 'string' && /^\d+$/.test(item)) return parseInt(item, 10);
          // Legacy string name - find the neighborhood by name
          const found = data.neighborhoods.find(n => n.name === item);
          return found ? found.id : null;
        }).filter(id => id !== null);
        setDefaultNeighborhoods(convertedIds);
      }
    } catch (err) {
      console.error("Error loading neighborhoods:", err);
    }
  };

  const handleSaveNeighborhoods = async () => {
    setSavingNeighborhoods(true);
    setMessage({ type: "", text: "" });

    try {
      await authFetch("/profile", {
        method: "PUT",
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          default_neighborhoods: defaultNeighborhoods,
        }),
      });
      setMessage({ type: "success", text: "Default neighborhoods saved!" });
    } catch (err) {
      console.error("Error saving neighborhoods:", err);
      setMessage({ type: "error", text: "Failed to save neighborhoods" });
    } finally {
      setSavingNeighborhoods(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const data = await authFetch("/profile/notifications");
      const prefs = {};
      for (const p of data.preferences || []) {
        prefs[p.notification_type] = {
          enabled: p.enabled,
          threshold: p.threshold,
          neighborhood_ids: p.neighborhood_ids || [],
        };
      }
      setNotifications(prev => ({ ...prev, ...prefs }));
    } catch (err) {
      console.error("Error loading notifications:", err);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    setMessage({ type: "", text: "" });
    try {
      const prefs = Object.entries(notifications).map(([type, val]) => ({
        notification_type: type,
        enabled: val.enabled,
        threshold: val.threshold,
        neighborhood_ids: val.neighborhood_ids,
      }));
      await authFetch("/profile/notifications", {
        method: "PUT",
        body: JSON.stringify({ preferences: prefs }),
      });
      setMessage({ type: "success", text: "Notification preferences saved!" });
    } catch (err) {
      console.error("Error saving notifications:", err);
      setMessage({ type: "error", text: "Failed to save notification preferences" });
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      await authFetch("/profile", {
        method: "PUT",
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
        }),
      });
      setMessage({ type: "success", text: "Profile updated successfully!" });
    } catch (err) {
      console.error("Error saving profile:", err);
      setMessage({ type: "error", text: "Failed to update profile" });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: "error", text: "Password must be at least 6 characters" });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      await authFetch("/profile/password", {
        method: "PUT",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      setMessage({ type: "success", text: "Password changed successfully!" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Error changing password:", err);
      setMessage({ type: "error", text: err.message || "Failed to change password" });
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: "error", text: "Image must be less than 5MB" });
        return;
      }
      setProfilePicture(file);
      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePictureUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadPicture = async () => {
    if (!profilePicture) return;

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const formData = new FormData();
      formData.append("file", profilePicture);

      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:8000"}/profile/picture`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      setProfilePictureUrl(data.profile_picture_url);
      setProfilePicture(null);
      setMessage({ type: "success", text: "Profile picture updated!" });
    } catch (err) {
      console.error("Error uploading picture:", err);
      setMessage({ type: "error", text: "Failed to upload picture" });
    } finally {
      setSaving(false);
    }
  };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
  };

  const containerStyle = {
    maxWidth: "900px",
    margin: "40px auto",
    padding: "0 20px",
  };

  const sectionStyle = {
    backgroundColor: "white",
    padding: "32px",
    borderRadius: "8px",
    marginBottom: "24px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  };

  const headingStyle = {
    fontSize: "24px",
    fontWeight: "600",
    marginBottom: "24px",
    borderBottom: "2px solid #000",
    paddingBottom: "12px",
  };

  const formGroupStyle = {
    marginBottom: "20px",
  };

  const labelStyle = {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "8px",
    color: "#333",
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "10px 24px",
    backgroundColor: "#000",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  };

  const disabledButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#ccc",
    cursor: "not-allowed",
  };

  const profilePictureStyle = {
    width: "150px",
    height: "150px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "4px solid #ddd",
  };

  const placeholderPictureStyle = {
    ...profilePictureStyle,
    backgroundColor: "#e0e0e0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "48px",
    color: "#999",
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <Header showLogout={true} />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: "18px", color: "#666" }}>Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <Header showLogout={true} />

      <div style={containerStyle}>
        {/* Message Display */}
        {message.text && (
          <div style={{
            padding: "12px 16px",
            marginBottom: "20px",
            borderRadius: "4px",
            backgroundColor: message.type === "success" ? "#d4edda" : "#f8d7da",
            color: message.type === "success" ? "#155724" : "#721c24",
            border: `1px solid ${message.type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
          }}>
            {message.text}
          </div>
        )}

        {/* Profile Picture Section */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>Profile Picture</h2>
          <div style={{ display: "flex", alignItems: "center", gap: "32px" }}>
            <div>
              {profilePictureUrl ? (
                <img src={profilePictureUrl} alt="Profile" style={profilePictureStyle} />
              ) : (
                <div style={placeholderPictureStyle}>
                  {firstName ? firstName.charAt(0).toUpperCase() : email.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                style={{ marginBottom: "12px" }}
              />
              <div style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
                Max file size: 5MB. Accepted formats: JPG, PNG, GIF
              </div>
              <button
                onClick={handleUploadPicture}
                disabled={!profilePicture || saving}
                style={!profilePicture || saving ? disabledButtonStyle : buttonStyle}
              >
                {saving ? "Uploading..." : "Upload Picture"}
              </button>
            </div>
          </div>
        </div>

        {/* Profile Details Section */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>Profile Details</h2>
          <form onSubmit={handleSaveProfile}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={email}
                disabled
                style={{ ...inputStyle, backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
              />
              <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                Email cannot be changed
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  style={inputStyle}
                  placeholder="Enter first name"
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  style={inputStyle}
                  placeholder="Enter last name"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              style={saving ? disabledButtonStyle : buttonStyle}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>

        {/* Default Neighborhoods Section */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>Default Neighborhoods</h2>
          <p style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
            Select neighborhoods to see by default when viewing sale reviews. If none selected, all neighborhoods will be shown (limited to 200 listings).
          </p>

          <div style={{ position: "relative", marginBottom: "20px" }}>
            <div
              onClick={() => setNeighborhoodDropdownOpen(!neighborhoodDropdownOpen)}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
                backgroundColor: defaultNeighborhoods.length > 0 ? "#e3f2fd" : "white",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                minHeight: "40px",
                boxSizing: "border-box"
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                {defaultNeighborhoods.length === 0
                  ? "Click to select neighborhoods..."
                  : defaultNeighborhoods.length === 1
                  ? availableNeighborhoods.find(n => n.id === defaultNeighborhoods[0])?.name || "1 neighborhood"
                  : `${defaultNeighborhoods.length} neighborhoods selected`}
              </span>
              <span style={{ marginLeft: "8px", color: "#666" }}>{neighborhoodDropdownOpen ? "▲" : "▼"}</span>
            </div>

            {neighborhoodDropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  backgroundColor: "white",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  zIndex: 1000,
                  maxHeight: "300px",
                  overflowY: "auto"
                }}
              >
                {/* Clear button */}
                <div style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDefaultNeighborhoods([]);
                    }}
                    style={{
                      padding: "4px 8px",
                      fontSize: "12px",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #ddd",
                      borderRadius: "3px",
                      cursor: "pointer"
                    }}
                  >
                    Clear All
                  </button>
                </div>
                {/* Neighborhood list */}
                {[...availableNeighborhoods].sort((a, b) => a.name.localeCompare(b.name)).map((n) => (
                  <label
                    key={n.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "10px 12px",
                      cursor: "pointer",
                      backgroundColor: defaultNeighborhoods.includes(n.id) ? "#e3f2fd" : "transparent",
                      borderBottom: "1px solid #f0f0f0"
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={defaultNeighborhoods.includes(n.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setDefaultNeighborhoods([...defaultNeighborhoods, n.id]);
                        } else {
                          setDefaultNeighborhoods(defaultNeighborhoods.filter(x => x !== n.id));
                        }
                      }}
                      style={{ marginRight: "10px" }}
                    />
                    <span style={{ flex: 1 }}>{n.name}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Click outside to close */}
            {neighborhoodDropdownOpen && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999
                }}
                onClick={() => setNeighborhoodDropdownOpen(false)}
              />
            )}
          </div>

          {/* Selected neighborhoods display */}
          {defaultNeighborhoods.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "13px", color: "#666", marginBottom: "8px" }}>Selected:</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {defaultNeighborhoods.map((id) => {
                  const neighborhood = availableNeighborhoods.find(n => n.id === id);
                  return (
                    <span
                      key={id}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "4px 10px",
                        backgroundColor: "#e3f2fd",
                        borderRadius: "16px",
                        fontSize: "13px"
                      }}
                    >
                      {neighborhood?.name || `ID: ${id}`}
                      <button
                        onClick={() => setDefaultNeighborhoods(defaultNeighborhoods.filter(x => x !== id))}
                        style={{
                          marginLeft: "6px",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "0",
                          fontSize: "14px",
                          color: "#666"
                        }}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <button
            onClick={handleSaveNeighborhoods}
            disabled={savingNeighborhoods}
            style={savingNeighborhoods ? disabledButtonStyle : buttonStyle}
          >
            {savingNeighborhoods ? "Saving..." : "Save Neighborhoods"}
          </button>
        </div>

        {/* Email Notifications Section */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>Email Notifications</h2>
          <p style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
            Configure email alerts for listings that match your criteria.
          </p>

          {/* High Cap Rate Notification */}
          <div style={{
            padding: "16px", border: "1px solid #e0e0e0", borderRadius: "8px", marginBottom: "16px",
            backgroundColor: notifications.high_cap_rate?.enabled ? "#f8fdf8" : "#fafafa",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: "15px" }}>High Cap Rate Alert</div>
                <div style={{ fontSize: "13px", color: "#666", marginTop: "2px" }}>
                  Get notified when a new listing exceeds your cap rate threshold
                </div>
              </div>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={notifications.high_cap_rate?.enabled || false}
                  onChange={(e) => setNotifications(prev => ({
                    ...prev,
                    high_cap_rate: { ...prev.high_cap_rate, enabled: e.target.checked },
                  }))}
                  style={{ width: "18px", height: "18px" }}
                />
                <span style={{ fontSize: "13px", fontWeight: 500, color: notifications.high_cap_rate?.enabled ? "#28a745" : "#666" }}>
                  {notifications.high_cap_rate?.enabled ? "Enabled" : "Enable"}
                </span>
              </label>
            </div>

                <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", marginBottom: "12px" }}>
                  <div>
                    <label style={{ ...labelStyle, marginBottom: "4px" }}>Min Cap Rate (%)</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="20"
                      value={notifications.high_cap_rate?.threshold ?? 5}
                      onChange={(e) => setNotifications(prev => ({
                        ...prev,
                        high_cap_rate: { ...prev.high_cap_rate, threshold: parseFloat(e.target.value) || 0 },
                      }))}
                      style={{ ...inputStyle, width: "100px" }}
                    />
                  </div>
                </div>

                {/* Neighborhood filter for notification */}
                <div>
                  <label style={{ ...labelStyle, marginBottom: "4px" }}>Neighborhoods (empty = all)</label>
                  <div style={{ position: "relative" }}>
                    <div
                      onClick={() => setNotifDropdownOpen(!notifDropdownOpen)}
                      style={{
                        padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px",
                        cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center",
                        backgroundColor: (notifications.high_cap_rate?.neighborhood_ids?.length || 0) > 0 ? "#e3f2fd" : "white",
                      }}
                    >
                      <span>
                        {(notifications.high_cap_rate?.neighborhood_ids?.length || 0) === 0
                          ? "All neighborhoods"
                          : `${notifications.high_cap_rate.neighborhood_ids.length} selected`}
                      </span>
                      <span style={{ color: "#666" }}>{notifDropdownOpen ? "▲" : "▼"}</span>
                    </div>

                    {notifDropdownOpen && (
                      <>
                        <div style={{
                          position: "absolute", top: "100%", left: 0, right: 0, backgroundColor: "white",
                          border: "1px solid #ddd", borderRadius: "4px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          zIndex: 1000, maxHeight: "250px", overflowY: "auto",
                        }}>
                          <div style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setNotifications(prev => ({
                                  ...prev,
                                  high_cap_rate: { ...prev.high_cap_rate, neighborhood_ids: [] },
                                }));
                              }}
                              style={{ padding: "3px 8px", fontSize: "11px", backgroundColor: "#f5f5f5", border: "1px solid #ddd", borderRadius: "3px", cursor: "pointer" }}
                            >Clear All</button>
                          </div>
                          {[...availableNeighborhoods].sort((a, b) => a.name.localeCompare(b.name)).map((n) => (
                            <label
                              key={n.id}
                              style={{
                                display: "flex", alignItems: "center", padding: "8px 12px", cursor: "pointer",
                                backgroundColor: (notifications.high_cap_rate?.neighborhood_ids || []).includes(n.id) ? "#e3f2fd" : "transparent",
                                borderBottom: "1px solid #f0f0f0", fontSize: "13px",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={(notifications.high_cap_rate?.neighborhood_ids || []).includes(n.id)}
                                onChange={(e) => {
                                  const ids = notifications.high_cap_rate?.neighborhood_ids || [];
                                  setNotifications(prev => ({
                                    ...prev,
                                    high_cap_rate: {
                                      ...prev.high_cap_rate,
                                      neighborhood_ids: e.target.checked ? [...ids, n.id] : ids.filter(x => x !== n.id),
                                    },
                                  }));
                                }}
                                style={{ marginRight: "8px" }}
                              />
                              {n.name}
                            </label>
                          ))}
                        </div>
                        <div
                          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                          onClick={() => setNotifDropdownOpen(false)}
                        />
                      </>
                    )}
                  </div>
                </div>

                {(notifications.high_cap_rate?.neighborhood_ids?.length || 0) > 0 && (
                  <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {notifications.high_cap_rate.neighborhood_ids.map((id) => {
                      const n = availableNeighborhoods.find(x => x.id === id);
                      return (
                        <span key={id} style={{
                          display: "inline-flex", alignItems: "center", padding: "3px 8px",
                          backgroundColor: "#e3f2fd", borderRadius: "12px", fontSize: "12px",
                        }}>
                          {n?.name || `ID: ${id}`}
                          <button
                            onClick={() => setNotifications(prev => ({
                              ...prev,
                              high_cap_rate: {
                                ...prev.high_cap_rate,
                                neighborhood_ids: prev.high_cap_rate.neighborhood_ids.filter(x => x !== id),
                              },
                            }))}
                            style={{ marginLeft: "4px", background: "none", border: "none", cursor: "pointer", padding: "0", fontSize: "13px", color: "#666" }}
                          >×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
          </div>

          {/* Below Previous Sale Notification */}
          <div style={{
            padding: "16px", border: "1px solid #e0e0e0", borderRadius: "8px", marginBottom: "16px",
            backgroundColor: notifications.below_prev_sale?.enabled ? "#fdf8f8" : "#fafafa",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: "15px" }}>Below Previous Sale Alert</div>
                <div style={{ fontSize: "13px", color: "#666", marginTop: "2px" }}>
                  Get notified when a condo is listed below its previous sale price
                </div>
              </div>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={notifications.below_prev_sale?.enabled || false}
                  onChange={(e) => setNotifications(prev => ({
                    ...prev,
                    below_prev_sale: { ...prev.below_prev_sale, enabled: e.target.checked },
                  }))}
                  style={{ width: "18px", height: "18px" }}
                />
                <span style={{ fontSize: "13px", fontWeight: 500, color: notifications.below_prev_sale?.enabled ? "#28a745" : "#666" }}>
                  {notifications.below_prev_sale?.enabled ? "Enabled" : "Enable"}
                </span>
              </label>
            </div>

                {/* Neighborhood filter for below prev sale */}
                <div>
                  <label style={{ ...labelStyle, marginBottom: "4px" }}>Neighborhoods (empty = all)</label>
                  <div style={{ position: "relative" }}>
                    <div
                      onClick={() => setBelowSaleDropdownOpen(!belowSaleDropdownOpen)}
                      style={{
                        padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px",
                        cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center",
                        backgroundColor: (notifications.below_prev_sale?.neighborhood_ids?.length || 0) > 0 ? "#e3f2fd" : "white",
                      }}
                    >
                      <span>
                        {(notifications.below_prev_sale?.neighborhood_ids?.length || 0) === 0
                          ? "All neighborhoods"
                          : `${notifications.below_prev_sale.neighborhood_ids.length} selected`}
                      </span>
                      <span style={{ color: "#666" }}>{belowSaleDropdownOpen ? "▲" : "▼"}</span>
                    </div>

                    {belowSaleDropdownOpen && (
                      <>
                        <div style={{
                          position: "absolute", top: "100%", left: 0, right: 0, backgroundColor: "white",
                          border: "1px solid #ddd", borderRadius: "4px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          zIndex: 1000, maxHeight: "250px", overflowY: "auto",
                        }}>
                          <div style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setNotifications(prev => ({
                                  ...prev,
                                  below_prev_sale: { ...prev.below_prev_sale, neighborhood_ids: [] },
                                }));
                              }}
                              style={{ padding: "3px 8px", fontSize: "11px", backgroundColor: "#f5f5f5", border: "1px solid #ddd", borderRadius: "3px", cursor: "pointer" }}
                            >Clear All</button>
                          </div>
                          {[...availableNeighborhoods].sort((a, b) => a.name.localeCompare(b.name)).map((n) => (
                            <label
                              key={n.id}
                              style={{
                                display: "flex", alignItems: "center", padding: "8px 12px", cursor: "pointer",
                                backgroundColor: (notifications.below_prev_sale?.neighborhood_ids || []).includes(n.id) ? "#e3f2fd" : "transparent",
                                borderBottom: "1px solid #f0f0f0", fontSize: "13px",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={(notifications.below_prev_sale?.neighborhood_ids || []).includes(n.id)}
                                onChange={(e) => {
                                  const ids = notifications.below_prev_sale?.neighborhood_ids || [];
                                  setNotifications(prev => ({
                                    ...prev,
                                    below_prev_sale: {
                                      ...prev.below_prev_sale,
                                      neighborhood_ids: e.target.checked ? [...ids, n.id] : ids.filter(x => x !== n.id),
                                    },
                                  }));
                                }}
                                style={{ marginRight: "8px" }}
                              />
                              {n.name}
                            </label>
                          ))}
                        </div>
                        <div
                          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                          onClick={() => setBelowSaleDropdownOpen(false)}
                        />
                      </>
                    )}
                  </div>
                </div>

                {(notifications.below_prev_sale?.neighborhood_ids?.length || 0) > 0 && (
                  <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {notifications.below_prev_sale.neighborhood_ids.map((id) => {
                      const n = availableNeighborhoods.find(x => x.id === id);
                      return (
                        <span key={id} style={{
                          display: "inline-flex", alignItems: "center", padding: "3px 8px",
                          backgroundColor: "#e3f2fd", borderRadius: "12px", fontSize: "12px",
                        }}>
                          {n?.name || `ID: ${id}`}
                          <button
                            onClick={() => setNotifications(prev => ({
                              ...prev,
                              below_prev_sale: {
                                ...prev.below_prev_sale,
                                neighborhood_ids: prev.below_prev_sale.neighborhood_ids.filter(x => x !== id),
                              },
                            }))}
                            style={{ marginLeft: "4px", background: "none", border: "none", cursor: "pointer", padding: "0", fontSize: "13px", color: "#666" }}
                          >×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
          </div>

          <button
            onClick={handleSaveNotifications}
            disabled={savingNotifications}
            style={savingNotifications ? disabledButtonStyle : buttonStyle}
          >
            {savingNotifications ? "Saving..." : "Save Notification Preferences"}
          </button>
        </div>

        {/* Change Password Section */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>Change Password</h2>
          <form onSubmit={handleChangePassword}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                style={inputStyle}
                placeholder="Enter current password"
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={inputStyle}
                placeholder="Enter new password (min 6 characters)"
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={inputStyle}
                placeholder="Confirm new password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              style={saving ? disabledButtonStyle : buttonStyle}
            >
              {saving ? "Changing..." : "Change Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
